import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  ALL_SCOPES,
  adminFetch,
  apiFetch,
  clearSession,
  getAdminSecret,
  getApiBase,
  getBearer,
  setAdminSecret,
  setBearer,
  type Scope,
} from "./lib/api";

type Tab = "overview" | "posts" | "keys" | "playground" | "docs";

type Stats = {
  posts: number;
  channels: number;
  attachments: number;
  activeApiKeys: number;
  failedWebhookEvents: number;
  recentWebhookEvents: Array<{
    id: string;
    telegramUpdateId: number;
    type: string;
    status: string;
    error: string | null;
    createdAt: string;
  }>;
};

type ApiKeyRow = {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  revokedAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
};

type MediaItem = {
  id: string;
  type: string;
  mimeType: string | null;
  url: string | null;
};

type PostSummary = {
  id: string;
  body?: string;
  bodyPlain?: string;
  bodyHtml?: string;
  publishedAt?: string;
  channel?: { id: string; name: string };
  hasMedia?: boolean;
  mediaCount?: number;
  tags?: string[];
  media?: MediaItem[];
};

type PostFull = PostSummary & {
  text?: string | null;
  caption?: string | null;
  status?: string;
  telegramMessageId?: number;
  updatedAt?: string;
  attachments?: Array<{
    id: string;
    type: string;
    mimeType: string | null;
    hasFile: boolean;
    url: string | null;
    urlExpiresAt: string | null;
  }>;
};

type Pagination = {
  mode: "page" | "cursor";
  page: number | null;
  limit: number;
  hasMore: boolean;
  total: number | null;
  totalPages: number | null;
  nextPage: number | null;
  nextCursor: string | null;
};

export function App() {
  const [ready, setReady] = useState(Boolean(getAdminSecret()));
  const [tab, setTab] = useState<Tab>("overview");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  if (!ready) {
    return (
      <Gate
        onUnlock={() => {
          setReady(true);
          setNotice("Sesión admin abierta en este navegador.");
        }}
      />
    );
  }

  return (
    <div className="app-shell">
      <header className="brand">
        <div>
          <h1>Club Codice Admin</h1>
          <p>
            Panel interno para tokens, posts ingeridos y documentación de la API.
            API: <span className="mono">{getApiBase()}</span>
          </p>
        </div>
        <button
          className="btn btn-ghost"
          type="button"
          onClick={() => {
            clearSession();
            setReady(false);
          }}
        >
          Cerrar sesión
        </button>
      </header>

      <nav className="nav">
        {(
          [
            ["overview", "Overview"],
            ["posts", "Posts"],
            ["keys", "API Keys"],
            ["playground", "Playground"],
            ["docs", "Docs API"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={tab === id ? "active" : ""}
            onClick={() => {
              setTab(id);
              setError("");
              setNotice("");
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      {error ? <div className="flash err">{error}</div> : null}
      {notice ? <div className="flash ok">{notice}</div> : null}

      {tab === "overview" ? <Overview onError={setError} /> : null}
      {tab === "posts" ? (
        <Posts onError={setError} onNotice={setNotice} />
      ) : null}
      {tab === "keys" ? (
        <ApiKeys onError={setError} onNotice={setNotice} />
      ) : null}
      {tab === "playground" ? <Playground onError={setError} /> : null}
      {tab === "docs" ? <Docs /> : null}
    </div>
  );
}

function Gate({ onUnlock }: { onUnlock: () => void }) {
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      setAdminSecret(secret.trim());
      await adminFetch("/admin/v1/stats");
      onUnlock();
    } catch (error) {
      clearSession();
      setErr(error instanceof Error ? error.message : "No se pudo entrar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gate">
      <form className="gate-card" onSubmit={submit}>
        <h1>Entrar al admin</h1>
        <p className="muted">
          Ingresa el token de acceso al panel para continuar.
        </p>
        <div className="form-row">
          <label htmlFor="secret">Token de acceso</label>
          <input
            id="secret"
            type="password"
            autoComplete="current-password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="Pega tu token aquí"
            required
          />
        </div>
        {err ? <div className="flash err">{err}</div> : null}
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? "Validando…" : "Abrir panel"}
        </button>
      </form>
    </div>
  );
}

function Overview({ onError }: { onError: (v: string) => void }) {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminFetch("/admin/v1/stats")
      .then((json) => {
        if (!cancelled) setStats(json.data as Stats);
      })
      .catch((e) => onError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [onError]);

  if (!stats) return <div className="panel empty">Cargando overview…</div>;

  return (
    <>
      <section className="panel">
        <h2>Resumen</h2>
        <p className="lede">Estado actual de la ingesta y las claves activas.</p>
        <div className="grid-stats">
          <div className="stat">
            <div className="label">Posts</div>
            <div className="value">{stats.posts}</div>
          </div>
          <div className="stat">
            <div className="label">Canales</div>
            <div className="value">{stats.channels}</div>
          </div>
          <div className="stat">
            <div className="label">Attachments</div>
            <div className="value">{stats.attachments}</div>
          </div>
          <div className="stat">
            <div className="label">API keys</div>
            <div className="value">{stats.activeApiKeys}</div>
          </div>
          <div className="stat">
            <div className="label">Webhook fails</div>
            <div className="value">{stats.failedWebhookEvents}</div>
          </div>
        </div>
      </section>

      <section className="panel">
        <h2>Últimos webhook events</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Update</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Error</th>
                <th>Creado</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentWebhookEvents.map((e) => (
                <tr key={e.id}>
                  <td className="mono">{e.telegramUpdateId}</td>
                  <td>{e.type}</td>
                  <td>
                    <span
                      className={`badge ${e.status === "COMPLETED" ? "ok" : e.status === "FAILED" ? "danger" : "warn"}`}
                    >
                      {e.status}
                    </span>
                  </td>
                  <td className="mono">{e.error ?? "—"}</td>
                  <td>{new Date(e.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function ApiKeys({
  onError,
  onNotice,
}: {
  onError: (v: string) => void;
  onNotice: (v: string) => void;
}) {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [name, setName] = useState("admin-ui");
  const [scopes, setScopes] = useState<Scope[]>([...ALL_SCOPES]);
  const [createdToken, setCreatedToken] = useState("");
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const json = await adminFetch("/admin/v1/api-keys");
    setKeys(json.data as ApiKeyRow[]);
  }

  useEffect(() => {
    refresh().catch((e) => onError(e instanceof Error ? e.message : String(e)));
  }, [onError]);

  async function createKey(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setCreatedToken("");
    try {
      const json = await adminFetch("/admin/v1/api-keys", {
        method: "POST",
        body: JSON.stringify({ name, scopes }),
      });
      setCreatedToken(json.data.token as string);
      setBearer(json.data.token as string);
      onNotice("Token creado. También se guardó como Bearer para el playground.");
      await refresh();
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    if (!confirm("¿Revocar esta API key?")) return;
    try {
      await adminFetch(`/admin/v1/api-keys/${id}/revoke`, { method: "POST" });
      onNotice("API key revocada.");
      await refresh();
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    }
  }

  return (
    <>
      <section className="panel">
        <h2>Crear API key</h2>
        <p className="lede">El token completo solo se muestra una vez.</p>
        <form onSubmit={createKey}>
          <div className="form-row">
            <label htmlFor="name">Nombre</label>
            <input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-row">
            <label>Scopes</label>
            <div className="scopes">
              {ALL_SCOPES.map((scope) => (
                <label key={scope}>
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope)}
                    onChange={(e) => {
                      setScopes((prev) =>
                        e.target.checked
                          ? [...prev, scope]
                          : prev.filter((s) => s !== scope),
                      );
                    }}
                  />
                  {scope}
                </label>
              ))}
            </div>
          </div>
          <div className="actions">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={busy || scopes.length === 0}
            >
              {busy ? "Creando…" : "Crear token"}
            </button>
          </div>
        </form>
        {createdToken ? (
          <div className="token-box">
            <div>Guárdalo ahora:</div>
            <code>{createdToken}</code>
          </div>
        ) : null}
      </section>

      <section className="panel">
        <h2>Keys existentes</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Prefix</th>
                <th>Scopes</th>
                <th>Estado</th>
                <th>Último uso</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td className="mono">{k.prefix}…</td>
                  <td>{k.scopes.join(", ")}</td>
                  <td>
                    <span className={`badge ${k.revokedAt ? "danger" : "ok"}`}>
                      {k.revokedAt ? "revoked" : "active"}
                    </span>
                  </td>
                  <td>
                    {k.lastUsedAt
                      ? new Date(k.lastUsedAt).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    {!k.revokedAt ? (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => void revoke(k.id)}
                      >
                        Revocar
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function Posts({
  onError,
  onNotice,
}: {
  onError: (v: string) => void;
  onNotice: (v: string) => void;
}) {
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [selected, setSelected] = useState<PostFull | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [view, setView] = useState<"summary" | "full" | "ai">("summary");
  const [hasMedia, setHasMedia] = useState<"all" | "true" | "false">("all");
  const [includeMedia, setIncludeMedia] = useState(true);
  const [includeTotal, setIncludeTotal] = useState(true);
  const [busy, setBusy] = useState(false);
  const bearer = getBearer();

  async function load(targetPage = page) {
    if (!getBearer()) {
      onError("Crea o pega un Bearer en API Keys / Playground para listar posts.");
      return;
    }
    setBusy(true);
    try {
      const qs = new URLSearchParams({
        limit: "10",
        page: String(targetPage),
        view,
        includeMedia: String(includeMedia),
        includeTotal: String(includeTotal),
      });
      if (hasMedia !== "all") qs.set("hasMedia", hasMedia);

      const json = await apiFetch(`/api/v1/posts?${qs}`);
      setPosts(json.data as PostSummary[]);
      setPagination(json.pagination as Pagination);
      setPage(targetPage);
      onNotice(
        `Página ${targetPage} · ${json.data.length} posts${
          json.pagination?.total != null
            ? ` · total ${json.pagination.total}`
            : ""
        } · view=${view}`,
      );
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (bearer) {
      load(1).catch((e) => onError(e instanceof Error ? e.message : String(e)));
    }
  }, []);

  async function openPost(id: string) {
    try {
      const json = await apiFetch(
        `/api/v1/posts/${id}?view=full&fields=body,bodyPlain,bodyHtml,tags,attachments,channel,publishedAt`,
      );
      setSelected(json.data as PostFull);
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    }
  }

  return (
    <>
      <section className="panel">
        <h2>Posts ingeridos</h2>
        <p className="lede">
          Elige un paquete (summary / ai / full) o mira Docs para armar{" "}
          <code>fields=…</code> a la carta.
        </p>

        <div className="filters">
          <label>
            Paquete de datos
            <select
              value={view}
              onChange={(e) =>
                setView(e.target.value as "summary" | "full" | "ai")
              }
            >
              <option value="summary">summary — pantallas</option>
              <option value="ai">ai — texto limpio para IA</option>
              <option value="full">full — todo</option>
            </select>
          </label>
          <label>
            Media
            <select
              value={hasMedia}
              onChange={(e) =>
                setHasMedia(e.target.value as "all" | "true" | "false")
              }
            >
              <option value="all">todas</option>
              <option value="true">con media</option>
              <option value="false">sin media</option>
            </select>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={includeMedia}
              onChange={(e) => setIncludeMedia(e.target.checked)}
            />
            Incluir URLs media
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={includeTotal}
              onChange={(e) => setIncludeTotal(e.target.checked)}
            />
            Contar total
          </label>
        </div>

        <div className="actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() =>
              void load(1).catch((e) =>
                onError(e instanceof Error ? e.message : String(e)),
              )
            }
          >
            {busy ? "Cargando…" : "Aplicar filtros"}
          </button>
          {pagination?.page && pagination.page > 1 ? (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() =>
                void load(page - 1).catch((e) =>
                  onError(e instanceof Error ? e.message : String(e)),
                )
              }
            >
              ← Anterior
            </button>
          ) : null}
          {pagination?.hasMore ||
          (pagination?.totalPages != null && page < pagination.totalPages) ? (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() =>
                void load(page + 1).catch((e) =>
                  onError(e instanceof Error ? e.message : String(e)),
                )
              }
            >
              Siguiente →
            </button>
          ) : null}
        </div>

        {pagination ? (
          <p className="muted">
            Página {pagination.page ?? page}
            {pagination.totalPages != null
              ? ` de ${pagination.totalPages}`
              : ""}
            {pagination.total != null ? ` · ${pagination.total} posts` : ""}
          </p>
        ) : null}

        {posts.length === 0 ? (
          <div className="empty">Sin posts o falta Bearer.</div>
        ) : (
          posts.map((p) => (
            <article key={p.id} className="post-card">
              <h3>
                {(p.bodyPlain || p.body || "").slice(0, 160) || "(sin texto)"}
                {(p.bodyPlain || p.body || "").length > 160 ? "…" : ""}
              </h3>
              <div className="muted">
                {p.channel?.name ?? "—"} ·{" "}
                {p.publishedAt
                  ? new Date(p.publishedAt).toLocaleString()
                  : "—"}{" "}
                ·{" "}
                {p.hasMedia
                  ? `${p.mediaCount ?? "?"} media`
                  : "sin media"}
              </div>
              {p.tags && p.tags.length > 0 ? (
                <div className="tag-row">
                  {p.tags.map((t) => (
                    <span key={t} className="badge">
                      #{t}
                    </span>
                  ))}
                </div>
              ) : null}
              {p.media && p.media.length > 0 ? (
                <div className="media-row">
                  {p.media.map((m) =>
                    m.url && m.type === "photo" ? (
                      <a
                        key={m.id}
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          className="media-thumb"
                          src={m.url}
                          alt={m.id}
                        />
                      </a>
                    ) : m.url ? (
                      <a
                        key={m.id}
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mono"
                      >
                        {m.type} · abrir
                      </a>
                    ) : null,
                  )}
                </div>
              ) : null}
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => void openPost(p.id)}
                >
                  Ver detalle
                </button>
              </div>
            </article>
          ))
        )}
      </section>

      {selected ? (
        <section className="panel">
          <h2>Detalle</h2>
          <p className="mono">{selected.id}</p>
          {selected.bodyHtml ? (
            <div
              className="body-html"
              dangerouslySetInnerHTML={{ __html: selected.bodyHtml }}
            />
          ) : (
            <pre className="code-block">
              {selected.bodyPlain || selected.body || "(sin texto)"}
            </pre>
          )}
          {selected.tags && selected.tags.length > 0 ? (
            <div className="tag-row">
              {selected.tags.map((t) => (
                <span key={t} className="badge">
                  #{t}
                </span>
              ))}
            </div>
          ) : null}
          {(selected.attachments ?? []).map((a) => (
            <div key={a.id} className="media-detail">
              <span className="badge">{a.type}</span>{" "}
              <span className="mono">{a.id}</span>
              {a.url ? (
                <div>
                  {a.type === "photo" ? (
                    <img className="media-thumb" src={a.url} alt={a.id} />
                  ) : null}
                  <div>
                    <a href={a.url} target="_blank" rel="noreferrer">
                      Abrir archivo firmado
                    </a>
                    {a.urlExpiresAt ? (
                      <span className="muted">
                        {" "}
                        · expira {new Date(a.urlExpiresAt).toLocaleDateString()}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}
    </>
  );
}

function Playground({ onError }: { onError: (v: string) => void }) {
  const [bearer, setBearerState] = useState(getBearer());
  const [path, setPath] = useState(
    "/api/v1/posts?limit=5&page=1&view=ai&includeTotal=true",
  );
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);

  const examples = useMemo(
    () => [
      "/api/v1/posts?limit=5&page=1&view=summary&includeTotal=true",
      "/api/v1/posts?limit=5&page=1&view=ai",
      "/api/v1/posts?page=1&fields=bodyPlain,tags,media",
      "/api/v1/posts?limit=5&page=1&view=full&hasMedia=true",
      "/api/v1/search?q=test&page=1&view=ai",
      "/api/v1/channels",
    ],
    [],
  );

  async function run(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setResult("");
    try {
      setBearer(bearer.trim());
      const json = await apiFetch(path.startsWith("/") ? path : `/${path}`);
      setResult(JSON.stringify(json, null, 2));
    } catch (error) {
      onError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Playground</h2>
      <p className="lede">Prueba endpoints de consulta con tu Bearer.</p>
      <form onSubmit={run}>
        <div className="form-row">
          <label htmlFor="bearer">Bearer token</label>
          <input
            id="bearer"
            value={bearer}
            onChange={(e) => setBearerState(e.target.value)}
            placeholder="api_live_…"
            required
          />
        </div>
        <div className="form-row">
          <label htmlFor="path">Path</label>
          <input
            id="path"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            required
          />
        </div>
        <div className="actions">
          {examples.map((ex) => (
            <button
              key={ex}
              type="button"
              className="btn btn-ghost"
              onClick={() => setPath(ex)}
            >
              {ex.length > 48 ? `${ex.slice(0, 48)}…` : ex}
            </button>
          ))}
        </div>
        <div className="actions">
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Llamando…" : "Ejecutar GET"}
          </button>
        </div>
      </form>
      {result ? (
        <div className="form-row" style={{ marginTop: 16 }}>
          <label>Respuesta</label>
          <textarea readOnly value={result} />
        </div>
      ) : null}
    </section>
  );
}

function Docs() {
  return (
    <section className="panel docs">
      <h2>Documentación (lenguaje sencillo)</h2>
      <p className="lede">
        Guía completa en el repo: <code>docs/api.md</code>. Aquí va lo esencial.
      </p>

      <h3>1. Dirección y contraseña</h3>
      <p className="mono">{getApiBase()}</p>
      <p>
        En cada petición:{" "}
        <code>Authorization: Bearer api_live_…</code> (tu token de API Keys).
      </p>

      <h3>2. Elige qué datos quieres de cada post</h3>
      <p>
        No hace falta pedir todo. Usa un <strong>paquete</strong> o arma tu lista.
      </p>
      <ul>
        <li>
          <code>view=summary</code> — para pantallas: texto normal (
          <code>body</code>), fecha, canal, tags, fotos.
        </li>
        <li>
          <code>view=ai</code> — para inteligencia artificial: texto limpio (
          <code>bodyPlain</code>), sin HTML ni caracteres raros, + tags y media.
        </li>
        <li>
          <code>view=full</code> — todo lo disponible.
        </li>
        <li>
          <code>fields=bodyPlain,tags,media</code> — solo lo que escribas
          (a la carta). El <code>id</code> siempre viene.
        </li>
      </ul>

      <h3>3. Tres formas del texto</h3>
      <ul>
        <li>
          <code>body</code> — tal cual se publicó.
        </li>
        <li>
          <code>bodyHtml</code> — para mostrar en una página web.
        </li>
        <li>
          <code>bodyPlain</code> — <strong>recomendado para IA</strong>: limpio,
          sin HTML.
        </li>
      </ul>

      <h3>4. Páginas</h3>
      <p>
        Empieza con <code>page=1&amp;limit=10</code>. Si hay más, usa{" "}
        <code>page=2</code>. Con <code>includeTotal=true</code> también te dice
        cuántas páginas hay en total.
      </p>

      <h3>5. Campos que puedes pedir en <code>fields</code></h3>
      <p className="mono" style={{ lineHeight: 1.7 }}>
        body · bodyPlain · bodyHtml · text · caption · publishedAt · updatedAt ·
        editedAt · status · telegramMessageId · authorSignature · mediaGroupId ·
        channel · hasMedia · mediaCount · tags · media · attachments
      </p>

      <h3>6. Recetas listas</h3>
      <pre className="code-block">{`# Pantalla / listado
${getApiBase()}/api/v1/posts?page=1&limit=10&view=summary&includeTotal=true

# Para una IA (recomendado)
${getApiBase()}/api/v1/posts?page=1&limit=20&view=ai

# Solo texto limpio + tags (muy liviano)
${getApiBase()}/api/v1/posts?page=1&fields=bodyPlain,tags,publishedAt

# Solo posts con foto
${getApiBase()}/api/v1/posts?page=1&view=ai&hasMedia=true
`}</pre>

      <h3>7. Fotos</h3>
      <p>
        Si pides <code>media</code>, cada ítem trae una <code>url</code> firmada
        (~1 año). Se puede abrir sin el token Bearer.
      </p>
    </section>
  );
}
